import { CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { FC } from 'react';

import AutocompleteLockersInput from '@components/Inputs/AutocompleteLockers/AutocompleteLockers';
import isString from 'lodash/isString';
import { toast } from 'sonner';
import InfoLine from 'src/components/InfoLine';
import { useBarcodeScanner } from 'src/hooks/useBarcodeReader';
import { useGetAllLockersBanksMapQuery } from 'src/redux/api/lockers';
import { AssignmentStatus, Piece, TrackingNumberStatus } from 'src/types/apiContract';
import { isPieceInLocker } from 'src/utils/piece';

import { sxSection } from '../styles';
import { LocationLockersError, LocationState, LocationStateChange } from '../useLocationId';
import CellsSizeSelect from './CellsSizeSelect';

type Props = {
  error: LocationLockersError;
  piece: Piece;
  spot: LocationState;
  onChange: (values: LocationStateChange) => void;
};

const lockerIdKeys = ['manufactureID', 'b'];
const cellIdKeys = ['manufacturerLockerID', 'c'];

const parseScannedCode = (str: string) => {
  const a = str.split(',');
  return a
    .map((param) => {
      // eslint-disable-next-line
      let [z, x] = param.split(':');
      if (z[0] !== '"') {
        z = `"${z}"`;
      }
      return [z, x].join(':');
    })
    .join(',');
};

const LockerContent: FC<Props> = ({ piece, spot, onChange, error }) => {
  const { data, isLoading } = useGetAllLockersBanksMapQuery();
  const isInLocker = isPieceInLocker(piece);

  useBarcodeScanner(
    (scan) => {
      if (isLoading) return;

      try {
        const parsedJson = scan[0] === '{' ? JSON.parse(scan) : JSON.parse(`{${parseScannedCode(scan)}}`);
        const lockerId = parsedJson[lockerIdKeys.find((key) => parsedJson[key]) || ''];
        const lockerCell = parsedJson[cellIdKeys.find((key) => parsedJson[key]) || ''];

        if (!isString(lockerId) || !isString(lockerCell) || !data) {
          throw new Error('error');
        }

        const item = Object.values(data).find((locker) => locker.externalId === lockerId);

        if (!item) {
          throw new Error('error');
        }

        onChange({
          lockerBankId: item.id,
          lockerCell,
        });
      } catch (err) {
        toast.error('Unable to identify locker information');
      }
    },
    undefined,
    { withPrint: false, maxInterval: 300, listenerId: 'barcode-reader-listener-id' },
  );

  const selectedLockerBank = spot?.lockerBankId ? data?.[spot?.lockerBankId] : null;

  if (isLoading) {
    return <CircularProgress aria-label="Loading..." sx={{ alignSelf: 'center' }} />;
  }

  const isAssigned = piece.pieceSummary?.pieceAssignmentStatus === AssignmentStatus.Assigned;
  const isBarcoded = piece.pieceSummary?.trackingNumberStatus === TrackingNumberStatus.Valid;

  const isDisabled = !isAssigned || !isBarcoded || isInLocker;

  return (
    <>
      <Stack sx={sxSection} spacing={3}>
        <Stack direction="row" spacing={0.8}>
          <Typography variant="subtitle1">Storage Group</Typography>
        </Stack>
        <AutocompleteLockersInput
          multiple={false}
          disabled={isDisabled}
          key={spot.lockerBankId}
          onChange={(ids) => {
            const id = ids[0];
            const nextBank = data?.[id];
            if (!nextBank) {
              onChange({});
              return;
            }
            const additional = nextBank.isRearLoaded ? { lockerCell: spot.lockerCell } : {};
            onChange({
              lockerBankId: id,
              ...additional,
            });
          }}
          error={error.lockerBankId}
          selectDefaultIfOneOption={isAssigned && isBarcoded}
          defaultValue={spot.lockerBankId}
        />
        {(!isAssigned || !isBarcoded) && (
          <Stack spacing={1}>
            {!isAssigned && <InfoLine text="Waiting to be assigned" />}
            {!isBarcoded && <InfoLine text="Waiting for unique tracking number" />}
          </Stack>
        )}
      </Stack>
      {selectedLockerBank && (
        <Stack sx={sxSection} spacing={3}>
          {selectedLockerBank.isRearLoaded ? (
            <>
              <Stack direction="row" spacing={0.8}>
                <Typography variant="subtitle1">Item</Typography>
              </Stack>
              <TextField
                disabled={isDisabled}
                value={spot.lockerCell || ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onChange({
                    lockerBankId: spot.lockerBankId,
                    lockerCell: event.target.value,
                  });
                }}
                error={!!error.lockerCell}
                helperText={error.lockerCell}
                fullWidth
                label="Locker Cell"
                required
              />
            </>
          ) : (
            <CellsSizeSelect
              lockerBank={selectedLockerBank}
              selectedCellSize={spot.lockerCellSize}
              disabled={isDisabled}
              onChange={(cellSize) =>
                onChange({
                  lockerBankId: spot.lockerBankId,
                  lockerCellSize: cellSize,
                })
              }
            />
          )}
        </Stack>
      )}
    </>
  );
};

export default LockerContent;
